import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from './use-api-client.js';
import type { InvitationResponse, MailVerdict } from '../api/types.js';

/** The last week of the operator's mail as the API read and triaged it (ADR-036); read again every 5 minutes while open */
export function useMail(serverId: string) {
  const api = useApiClient();
  return useQuery({ queryKey: ['mail', serverId], queryFn: () => api.mail.list(serverId), refetchInterval: 5 * 60_000, staleTime: 60_000 });
}

/** The operator's word on a message: the triage learns from it */
export function useMailVerdict(serverId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, verdict }: { key: string; verdict: MailVerdict }) => api.mail.verdict(serverId, key, verdict),
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['mail', serverId] }); },
  });
}

/** Create the draft: the thread's reply as edited, as a Gmail draft the operator sends himself */
export function useCreateDraft(serverId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, body }: { threadId: string; body: string }) => api.mail.createDraft(serverId, threadId, body),
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['mail', serverId] }); },
  });
}

/** Accept, Tentative or Decline: his answer on the event, the organiser notified (ADR-036 section 7) */
export function useRespondInvitation(serverId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, response }: { threadId: string; response: InvitationResponse }) => api.mail.respond(serverId, threadId, response),
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['mail', serverId] }); },
  });
}

/** Answer with a session: the thread's session for this mail, whatever the triage said */
export function useAskMailSession(serverId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key }: { key: string }) => api.mail.askSession(serverId, key),
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['mail', serverId] }); },
  });
}

/** A sender he marked as noise, removed: the triage reads its mail again */
export function useRemoveNoise(serverId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sender }: { sender: string }) => api.mail.removeNoise(serverId, sender),
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['mail', serverId] }); },
  });
}
